// fork from https://github.com/mikelpmc/react-multiline-clamping
import React, { useEffect, useState } from "react";

import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import TruncatedElement from "./partials/truncatedElement.component";

type ShowMoreElementProps = {
    toggle: () => void;
};

type ShowLessElementProps = {
    toggle: () => void;
};

const isCssEllipsisApplied = (elem: HTMLElement) =>
    elem.scrollHeight > elem.clientHeight;

const defaultShowMoreElement = ({ toggle }: ShowMoreElementProps) => {
    const { t } = useGranite();
    return (
        <button
            type="button"
            onClick={toggle}
        >
            {t("textComponent.link.showMore")}
        </button>
    );
};

const defaultShowLessElement = ({ toggle }: ShowLessElementProps) => {
    const { t } = useGranite();
    return (
        <button
            type="button"
            onClick={toggle}
        >
            {t("textComponent.link.showLess")}
        </button>
    );
};

type ClampProps = {
    children?: React.ReactNode;
    lines: number;
    maxLines?: number;
    withTooltip?: boolean;
    withToggle?: boolean;
    showMoreElement?: (props: ShowMoreElementProps) => React.ReactNode;
    showLessElement?: (props: ShowLessElementProps) => React.ReactNode;
    onShowMore?: (isExpanded: boolean) => any;
};

const Clamp = ({
    children,
    lines = 2,
    maxLines = 999,
    withTooltip = true,
    withToggle = false,
    showMoreElement = defaultShowMoreElement,
    showLessElement = defaultShowLessElement,
    onShowMore = () => {}
}: ClampProps) => {
    const [sLines, setLines] = useState(lines);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showMore, setShowMore] = useState(false);

    const handleToggleShowMore = (show: boolean) => {
        const newLines = show ? maxLines : lines;

        setShowMore((showMore) => !showMore);
        setIsExpanded((isExpanded) => !isExpanded);
        setLines(newLines);

        onShowMore(show);
    };

    const handleConfigElement = (elem: HTMLElement) => {
        if (!elem) return;

        if (isCssEllipsisApplied(elem)) {
            if (withTooltip) {
                const title = elem.textContent;
                elem.setAttribute("title", title || "");
            }

            if (withToggle && !showMore && !isExpanded) {
                setShowMore(true);
            }
        } else {
            elem.removeAttribute("title");
            setShowMore(false);
        }
    };

    useEffect(() => {
        if (lines) {
            setLines(lines);
        }
    }, [lines]);

    return (
        <>
            <TruncatedElement
                lines={sLines}
                getRef={handleConfigElement}
            >
                {children}
            </TruncatedElement>

            {showMore &&
                !isExpanded &&
                showMoreElement({ toggle: () => handleToggleShowMore(true) })}

            {isExpanded &&
                showLessElement({ toggle: () => handleToggleShowMore(false) })}
        </>
    );
};

export { Clamp };
